import React, { useState, useEffect } from 'react';
import { RefreshCw, Filter } from 'lucide-react';
import { DataTable, Column } from '@/components/ui';
import { useDateFormat } from '@/hooks/useDateFormat';

interface ModemResponse {
  id: string;
  deviceId: string;
  modemId?: string;
  port?: string;
  command: string;
  response: string;
  timestamp: Date;
}

interface ModemResponsesTableProps {
  deviceId: string;
  responses: ModemResponse[];
  loading?: boolean;
  onRefresh?: () => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const ModemResponsesTable: React.FC<ModemResponsesTableProps> = ({
  deviceId,
  responses,
  loading = false,
  onRefresh,
  autoRefresh = true,
  refreshInterval = 5000
}) => {
  const [modemFilter, setModemFilter] = useState('all');
  const fmt = useDateFormat();

  // Auto-refresh logic
  useEffect(() => {
    if (autoRefresh && onRefresh && refreshInterval > 0) {
      const interval = setInterval(onRefresh, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, onRefresh, refreshInterval]);

  // Get unique modems from responses (with null safety)
  const uniqueModems = Array.from(
    new Set((responses || []).map(r => r.modemId || r.port).filter(Boolean))
  );

  // Filter responses by modem
  const filteredResponses = (responses || []).filter(response => {
    if (modemFilter !== 'all') {
      if (response.modemId !== modemFilter && response.port !== modemFilter) {
        return false;
      }
    }
    return true;
  });

  // Define table columns
  const columns: Column<ModemResponse>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (_, row) => fmt.formatDateTime(row.timestamp)
    },
    {
      key: 'modemId',
      header: 'Modem',
      render: (_, row) => (
        <span className="px-2 py-1 text-xs font-medium bg-muted text-foreground rounded-full">
          {row.modemId || row.port || 'Unknown'}
        </span>
      )
    },
    {
      key: 'command',
      header: 'Command',
      sortable: true,
      render: (_, row) => (
        <code className="text-sm font-mono font-semibold text-foreground">
          {row.command}
        </code>
      )
    },
    {
      key: 'response',
      header: 'Response',
      render: (_, row) => (
        <pre className="text-sm font-mono text-foreground whitespace-pre-wrap break-all max-w-md">
          {row.response}
        </pre>
      )
    }
  ];

  return (
    <div className="space-y-4">

      {/* Filter and status bar */}
      <div className="bg-muted rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {/* Modem filter */}
            {uniqueModems.length > 1 && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground/70" />
                <select
                  value={modemFilter}
                  onChange={(e) => setModemFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-border rounded bg-background text-foreground"
                >
                  <option value="all">All Modems</option>
                  {uniqueModems.map(modemId => (
                    <option key={modemId} value={modemId as string}>{modemId}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Manual refresh button */}
            <button
              onClick={onRefresh}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-medium text-muted-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Status */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">
              Showing {filteredResponses.length} of {responses?.length || 0} responses
            </span>
            {autoRefresh && (
              <span className="flex items-center gap-1 text-muted-foreground/70">
                <div className="w-2 h-2 bg-status-success rounded-full animate-pulse" />
                Auto-refresh every {refreshInterval / 1000}s
              </span>
            )}
            {responses && responses.length >= 100 && (
              <span className="px-2 py-1 text-xs font-medium bg-accent-blue-faded text-accent-blue rounded">
                Max 100 responses stored
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredResponses}
        keyExtractor={(row) => row.id}
        searchable={true}
        searchPlaceholder="Search commands or responses..."
        emptyMessage={
          (!responses || responses.length === 0)
            ? 'No modem responses yet'
            : 'No responses match the current filters'
        }
      />
    </div>
  );
};
